import classnames from 'classnames'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Button from '../../components/basics/Button/Button'
import Checkbox from '../../components/basics/Checkbox/Checkbox'
import notificationComponents from '../../components/notificationComponents/notificationComponents'
import Pagination from '../../components/Pagination/Pagination'
import { Notification } from '../../models/Notification'
import useDeleteNotifications from '../../state/mutations/useDeleteNotifications'
import useMarkNotificationsRead from '../../state/mutations/useMarkNotificationsRead'
import useNotifications from '../../state/queries/useNotifications'
import useRoutes from '../../state/useRoutes'
import useItemSelection from '../../utilities/useItemSelection'
import { ItemSelectorInit } from '../../utilities/useItemSelector'

import styles from './NotificationCenterPage.module.scss'

/**********************************************************************
 * NotificationCenterPage
 **********************************************************************/
const notificationSelectionInit: ItemSelectorInit<Notification, Notification> = {
  createItem: (base) => base,
  limitToOptions: true,
}
export default React.memo(function NotificationCenterPage() {
  const { t } = useTranslation()

  const notificationFilter = useNotifications()

  const notificationSelection = useItemSelection(notificationSelectionInit, notificationFilter.notifications)

  // Mark as read
  const markNotificationsRead = useMarkNotificationsRead()
  const handleMarkRead = useCallback(
    () => markNotificationsRead.mutate({ notificationUuids: notificationSelection.items.map(({ uuid }) => uuid) }),
    [markNotificationsRead, notificationSelection.items]
  )

  // Delete
  const deleteNotifications = useDeleteNotifications()
  const handleDelete = useCallback(
    () => deleteNotifications.mutate({ notificationUuids: notificationSelection.items.map(({ uuid }) => uuid) }),
    [deleteNotifications, notificationSelection.items]
  )

  return (
    <>
      <div className={styles.NotificationCenterPage}>
        <header>
          <h1>{t('notifications.page title')}</h1>
        </header>

        <main>
          <div
            className={classnames(styles.notificationsTable, { skeleton: notificationFilter.result.isPlaceholderData })}
          >
            <table>
              <thead>
                <tr>
                  <th colSpan={4} className={styles.selectColumn}>
                    <div>
                      <Checkbox
                        omitErrorDisplay
                        onChange={notificationSelection.handleSelectAll}
                        checked={notificationSelection.items.length === notificationFilter.notifications.length}
                      />
                      <Button small onClick={handleMarkRead} disabled={!notificationSelection.items.length}>
                        {t('notifications.cta mark read')}
                      </Button>
                      <Button small onClick={handleDelete} disabled={!notificationSelection.items.length}>
                        {t('notifications.cta delete')}
                      </Button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {notificationFilter.notifications.map((notification) => {
                  const { Icon, Message, Action } =
                    notificationComponents[notification.type] || notificationComponents.Unknown

                  return (
                    <tr
                      key={notification.uuid}
                      className={classnames({
                        [styles.read]: Boolean(notification.readAt),
                        skeleton: notification.isDummy,
                      })}
                    >
                      <td className={styles.selectColumn}>
                        <Checkbox
                          omitErrorDisplay
                          value={notification.uuid}
                          onChange={notificationSelection.handleSelected}
                          checked={!!notificationSelection.ids[notification.uuid]}
                        >
                          <Icon {...({ notification: notification as any } as any)} />
                        </Checkbox>
                      </td>
                      <td className={styles.dateColumn}>{t('format.DATETIME', { date: notification.createdAt })}</td>
                      <td className={styles.messageColumn}>
                        <Message {...({ notification: notification as any } as any)} />
                      </td>
                      <td className={styles.actionColumn}>
                        <Action {...({ notification: notification as any } as any)} />
                      </td>
                    </tr>
                  )
                })}
                {!notificationFilter.notifications.length && (
                  <tr>
                    <td colSpan={4} className={styles.empty}>
                      {t('notifications.no notifications found')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination
              page={notificationFilter.page}
              pageCount={notificationFilter.pageCount}
              pageSize={notificationFilter.pageSize}
              itemCount={notificationFilter.itemCount}
              onPageChanged={notificationFilter.setPage}
            />
          </div>
        </main>
      </div>
    </>
  )
})

export function NotificationCenterPageBreadcrumb() {
  const { t } = useTranslation()
  const routes = useRoutes()
  return <Link to={routes.notifications.url({})}>{t('notifications.breadcrumb')}</Link>
}
